// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { ShieldQuestion, Loader2, AlertCircle, Filter, Plus } from 'lucide-react'
import * as api from '../../api/client'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { friendlyError } from '../../api/errors'

/**
 * /admin/data-requests — Data Subject Request (DSR) queue + intake + disposition.
 * Backend (PR-B1): GET/POST/PUT /api/ops/dsr.
 *
 * Intake is a STAFF action (the public legal page intentionally has no public form).
 * Resolution is admin-only; the server enforces the retention guard — a deletion that
 * reaches clinical/pharmacy records cannot be resolved as 'fulfilled' (returns 422), so
 * the UI surfaces that error rather than pretending the erasure happened.
 */

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '',                    label: 'All' },
  { value: 'received',            label: 'Received' },
  { value: 'in_review',           label: 'In review' },
  { value: 'fulfilled',           label: 'Fulfilled' },
  { value: 'partially_fulfilled', label: 'Partial' },
  { value: 'denied',              label: 'Denied' },
]
const REQUEST_TYPES = ['access', 'amendment', 'restriction', 'portability', 'deletion'] as const
const SCOPES = ['account', 'clinical', 'all'] as const
const RESOLUTIONS = ['in_review', 'fulfilled', 'partially_fulfilled', 'denied'] as const

// D-UI-2: what each column shows, for its header funnel. Keys are the lower-cased header labels.
const DSR_FUNNELS: FunnelColumn<api.DsrRecord>[] = [
  { key: 'subject', label: 'Subject', value: r => r.subject_label || r.subject_type },
  { key: 'type',    label: 'Type',    value: r => r.request_type },
  { key: 'scope',   label: 'Scope',   value: r => r.scope },
  { key: 'status',  label: 'Status',  value: r => r.status },
  { key: 'due',     label: 'Due',     value: r => r.due_at },
]

export default function DsrTab() {
  const [items, setItems] = useState<api.DsrRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showIntake, setShowIntake] = useState(false)

  // intake form state
  const [subjectType, setSubjectType] = useState<'patient' | 'user'>('patient')
  const [subjectLabel, setSubjectLabel] = useState('')
  const [requestType, setRequestType] = useState<(typeof REQUEST_TYPES)[number]>('access')
  const [scope, setScope] = useState<(typeof SCOPES)[number]>('account')
  const [detail, setDetail] = useState('')
  const [intakeNote, setIntakeNote] = useState<string | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listDsrRequests(statusFilter || undefined)
      setItems(r.items)
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { reload() }, [statusFilter])

  const deletionTouchesClinical = requestType === 'deletion' && scope !== 'account'

  async function submitIntake() {
    setBusyId('intake'); setError(null); setIntakeNote(null)
    try {
      const r = await api.createDsrRequest({
        subject_type: subjectType,
        subject_label: subjectLabel.trim() || undefined,
        request_type: requestType,
        scope,
        detail: detail.trim() || undefined,
      })
      if (r.note) setIntakeNote(r.note)
      setSubjectLabel(''); setDetail(''); setShowIntake(false)
      await reload()
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setBusyId(null)
    }
  }

  async function resolve(id: string, status: string) {
    let disposition: string | undefined
    let denial_basis: string | undefined
    if (status === 'denied' || status === 'partially_fulfilled') {
      disposition = window.prompt('Disposition note (what was done / why denied):') || undefined
    }
    setBusyId(id); setError(null)
    try {
      await api.resolveDsrRequest(id, { status, disposition, denial_basis })
      await reload()
    } catch (e) {
      // The retention guard surfaces here: a clinical-scope deletion resolved as
      // 'fulfilled' returns 422 deletion_of_retained_records_forbidden. Show it plainly.
      setError(friendlyError(e))
    } finally {
      setBusyId(null)
    }
  }

  const count = useMemo(() => items.length, [items])

  // Reset to page 1 when the status filter changes (a length resetKey misses two
  // different result sets that happen to have the same row count).
  // The header funnels filter the whole loaded set; paging happens after.
  const fx = useTableFunnels(items, DSR_FUNNELS)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${statusFilter}|${fx.sig}`)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <ShieldQuestion size={20} aria-hidden="true" />
        <h2 style={{ margin: 0, fontSize: 18 }}>Data subject requests</h2>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>{count} shown</span>
        <button onClick={() => setShowIntake(s => !s)} style={primaryBtn}>
          <Plus size={14} aria-hidden="true" /> Log a request
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={14} aria-hidden="true" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle} aria-label="Filter by status">
            {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
      </div>

      {intakeNote && (
        <div style={noteStyle} role="status">{intakeNote}</div>
      )}

      {showIntake && (
        <div style={intakeCard}>
          <div style={formRow}>
            <label style={lbl}>Subject
              <select value={subjectType} onChange={e => setSubjectType(e.target.value as any)} style={selectStyle}>
                <option value="patient">Patient</option>
                <option value="user">User</option>
              </select>
            </label>
            <label style={lbl}>Identifier (name / email)
              <input value={subjectLabel} onChange={e => setSubjectLabel(e.target.value)} style={inputStyle} placeholder="non-authoritative label" />
            </label>
          </div>
          <div style={formRow}>
            <label style={lbl}>Request type
              <select value={requestType} onChange={e => setRequestType(e.target.value as any)} style={selectStyle}>
                {REQUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label style={lbl}>Scope
              <select value={scope} onChange={e => setScope(e.target.value as any)} style={selectStyle}>
                {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label style={{ ...lbl, display: 'block' }}>Detail
            <textarea value={detail} onChange={e => setDetail(e.target.value)} style={{ ...inputStyle, width: '100%', minHeight: 60 }} placeholder="what the subject asked for" />
          </label>
          {deletionTouchesClinical && (
            <div style={warnStyle} role="status">
              Clinical/pharmacy records are subject to mandatory retention. A deletion at this scope
              cannot be fulfilled by erasure — it will be dispositioned as a denial with a statutory basis.
              Account (non-clinical) data can be deleted.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={submitIntake} disabled={busyId === 'intake'} style={primaryBtn}>
              {busyId === 'intake' ? <Loader2 size={14} className="spin" /> : null} Record request
            </button>
            <button onClick={() => setShowIntake(false)} style={ghostBtn}>Cancel</button>
          </div>
        </div>
      )}

      {error && (
        <div style={errStyle} role="alert">
          <AlertCircle size={16} aria-hidden="true" /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, color: 'var(--color-text-muted)' }}><Loader2 size={16} className="spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--color-text-muted)' }}>No requests.</div>
      ) : (
        <>
        {fx.chips}
        <div style={{ overflowX: "auto" }}>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="requests" />
          <table {...fx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                {['Subject', 'Type', 'Scope', 'Status', 'Due', 'Actions'].map(h => <th key={h} data-no-funnel={h === 'Actions' ? 'row actions' : undefined} style={thStyle}>{h} {fx.funnel(h.toLowerCase())}</th>)}
              </tr>
            </thead>
            <tbody>
              {pageRows.map(r => (
                <tr key={r.id}>
                  <td style={tdStyle}>{r.subject_label || <span style={{ color: 'var(--color-text-muted)' }}>{r.subject_type}</span>}</td>
                  <td style={tdStyle}>{r.request_type}</td>
                  <td style={tdStyle}>{r.scope}</td>
                  <td style={tdStyle}><span style={statusPill(r.status)}>{r.status}</span></td>
                  <td style={tdStyle}>{r.due_at ? new Date(r.due_at).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}>
                    {r.status === 'received' || r.status === 'in_review' ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {RESOLUTIONS.map(s => (
                          <button key={s} onClick={() => resolve(r.id, s)} disabled={busyId === r.id} style={miniBtn} title={s}>
                            {s.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                        {r.denial_basis ? `denied: ${r.denial_basis.slice(0, 40)}…` : 'resolved'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="requests" />
        </div>
        </>
      )}
    </div>
  )
}

const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--color-accent, #2563eb)', background: 'var(--color-accent, #2563eb)', color: '#fff', fontSize: 13, cursor: 'pointer' }
const ghostBtn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'transparent', fontSize: 13, cursor: 'pointer' }
const miniBtn: React.CSSProperties = { padding: '3px 8px', borderRadius: 5, border: '1px solid var(--color-border)', background: 'var(--color-surface, #fff)', fontSize: 11, cursor: 'pointer' }
const selectStyle: React.CSSProperties = { padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 13 }
const inputStyle: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 13 }
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--color-text-muted)', flex: 1 }
const formRow: React.CSSProperties = { display: 'flex', gap: 12, marginBottom: 10 }
const intakeCard: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginBottom: 16, background: 'var(--color-surface, #fff)' }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-muted)' }
const tdStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--color-border)' }
const errStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 6, background: 'var(--color-danger-bg, #fef2f2)', color: 'var(--color-danger, #b91c1c)', marginBottom: 12, fontSize: 13 }
const warnStyle: React.CSSProperties = { padding: '10px 12px', borderRadius: 6, background: 'var(--color-warn-bg, #fffbeb)', color: 'var(--color-warn-text, #92400e)', fontSize: 12, margin: '8px 0' }
const noteStyle: React.CSSProperties = { padding: '10px 12px', borderRadius: 6, background: 'var(--color-info-bg, #eff6ff)', color: 'var(--color-info-text, #1e40af)', fontSize: 13, marginBottom: 12 }

function statusPill(status: string): React.CSSProperties {
  const map: Record<string, string> = {
    received: '#3b82f6', in_review: '#a855f7', fulfilled: '#16a34a',
    partially_fulfilled: '#d97706', denied: '#dc2626',
  }
  const c = map[status] || '#6b7280'
  return { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, color: '#fff', background: c }
}
